import { useCallback, useEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $createParagraphNode, $createTextNode, $createLineBreakNode } from 'lexical';
import './llmPrompts.css';

// Loads the plain text once on mount, keeping single line breaks
// (prompt rows like the ❌/✅ examples are multi-line).
function HydratePlugin({ text }) {
  const [editor] = useLexicalComposerContext();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const para = $createParagraphNode();
      (text || '').split('\n').forEach((line, i) => {
        if (i > 0) para.append($createLineBreakNode());
        if (line) para.append($createTextNode(line));
      });
      root.append(para);
    }, { tag: 'history-merge' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/**
 * Compact plain-text Lexical editor for one prompt row. Uncontrolled: `value` is read
 * on mount only — remount (change the React key) to load new content.
 */
export default function LexicalRowInput({ value, onChange, placeholder }) {
  const handleChange = useCallback((editorState) => {
    editorState.read(() => onChange($getRoot().getTextContent()));
  }, [onChange]);

  const initialConfig = {
    namespace: 'llm-prompt-row',
    theme: {},
    onError: console.error,
  };

  return (
    <div className="position-relative">
      <LexicalComposer initialConfig={initialConfig}>
        <HydratePlugin text={value} />
        <HistoryPlugin />
        <PlainTextPlugin
          contentEditable={
            <ContentEditable className="llmp-editable" lang="fi" />
          }
          placeholder={
            <div className="llmp-placeholder">{placeholder}</div>
          }
        />
        <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
      </LexicalComposer>
    </div>
  );
}
